import { PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import {
  AuthService,
  FeedbackFabComponent,
  SettingsDialogComponent,
  StorageService,
  ToolbarPortalService,
} from '@suitelifetools/common';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PortalModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    FeedbackFabComponent,
  ],
  template: `
    <mat-sidenav-container class="sidenav-container">
      <mat-sidenav #drawer mode="over" class="app-sidenav">
        <mat-toolbar>Modules</mat-toolbar>
        <mat-nav-list>
          <a mat-list-item routerLink="/budget" (click)="drawer.close()">
            <mat-icon matListItemIcon>account_balance_wallet</mat-icon>
            <span matListItemTitle>Budget & Payday</span>
          </a>
          <!-- Reserved for future module -->
          <a mat-list-item disabled>
            <mat-icon matListItemIcon>restaurant_menu</mat-icon>
            <span matListItemTitle>Meal Planner (Soon)</span>
          </a>
          <a mat-list-item routerLink="/privacy" (click)="drawer.close()">
            <mat-icon matListItemIcon>privacy_tip</mat-icon>
            <span matListItemTitle>Privacy Policy</span>
          </a>
          <a mat-list-item routerLink="/terms" (click)="drawer.close()">
            <mat-icon matListItemIcon>gavel</mat-icon>
            <span matListItemTitle>Terms of Service</span>
          </a>
        </mat-nav-list>
      </mat-sidenav>

      <mat-sidenav-content class="sidenav-content-area">
        <!-- Pinned Sticky Top Toolbar -->
        <mat-toolbar class="app-toolbar">
          <div class="toolbar-brand">
            <button mat-icon-button (click)="drawer.toggle()" aria-label="Toggle navigation">
              <mat-icon>menu</mat-icon>
            </button>
            <span class="app-title">Suite Life Tools</span>
          </div>

          <!-- Dynamic Center Portal Outlet (matches content max-width) -->
          <div class="center-portal-container">
            <ng-template [cdkPortalOutlet]="toolbarService.activePortal()"></ng-template>
          </div>

          <!-- Right Section: User Profile -->
          <div class="toolbar-user">
            @if (auth.isAuthenticated()) {
              <div class="user-profile">
                <span class="greeting">Welcome, {{ auth.currentUser()?.name }}</span>
                <img
                  [src]="auth.currentUser()?.picture"
                  alt="Avatar"
                  class="avatar"
                  [matMenuTriggerFor]="userMenu"
                />
                <mat-menu #userMenu="matMenu">
                  <button mat-menu-item (click)="openSettingsDialog()">
                    <mat-icon>settings</mat-icon>
                    <span>Settings</span>
                  </button>
                  <button mat-menu-item (click)="auth.signOut()">
                    <mat-icon>logout</mat-icon>
                    <span>Sign Out</span>
                  </button>
                </mat-menu>
              </div>
            } @else {
              <div id="google-btn"></div>
            }
          </div>
        </mat-toolbar>

        <main class="content-viewport">
          <router-outlet></router-outlet>
        </main>

        <!-- Global Floating Feedback FAB -->
        <slt-feedback-fab />

        <!-- App Footer -->
        <footer class="app-footer">
          <div class="footer-inner">
            <div class="footer-left">
              <span class="brand-text">Suite Life Tools</span>
              <span class="separator">•</span>
              <span class="tagline">Local-First Personal Finance</span>
              <span class="separator">•</span>
              <span class="copyright">© {{ currentYear }}</span>
            </div>

            <div class="footer-right">
              <a routerLink="/privacy" class="footer-link">
                <mat-icon class="footer-icon">security</mat-icon>
                <span>Privacy Policy</span>
              </a>
              <a routerLink="/terms" class="footer-link">
                <mat-icon class="footer-icon">gavel</mat-icon>
                <span>Terms & Disclaimer</span>
              </a>
              <a routerLink="/budget/docs/auto-balance" class="footer-link">
                <mat-icon class="footer-icon">help_outline</mat-icon>
                <span>Documentation</span>
              </a>
            </div>
          </div>
        </footer>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: [
    `
      .sidenav-container {
        height: 100vh;
      }

      .app-sidenav {
        width: 250px;
      }

      .sidenav-content-area {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow-y: auto;
      }

      /* Pinned Sticky Toolbar */
      .app-toolbar {
        position: sticky;
        top: 0;
        z-index: 1000;
        display: flex;
        align-items: center;
        gap: 1.5rem;
        padding: 0 1.5rem;
        background: #ffffff;
        border-bottom: 1px solid #e0e0e0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        flex-shrink: 0;
      }

      .toolbar-brand {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-shrink: 0;

        .app-title {
          font-weight: 600;
          font-size: 1.15rem;
          white-space: nowrap;
        }
      }

      .center-portal-container {
        flex: 1 1 auto;
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 0;
        max-width: 1200px;
        margin: 0 auto;
      }

      .toolbar-user {
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .user-profile {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .greeting {
        font-size: 0.85rem;
        white-space: nowrap;
      }

      .avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        cursor: pointer;
        border: 2px solid #e0e0e0;
      }

      .content-viewport {
        flex: 1 0 auto;
        padding: 1rem;
      }

      /* App Footer & Sidenav Link Layout */
      .app-footer {
        flex-shrink: 0;
        background: #ffffff;
        border-top: 1px solid #e0e0e0;
        padding: 0.85rem 1.5rem;
        font-size: 0.82rem;
        color: #546e7a;
      }

      .footer-inner {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.75rem;
      }

      .footer-left {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-wrap: wrap;

        .brand-text {
          font-weight: 600;
          color: #263238;
        }

        .separator {
          color: #b0bec5;
        }
      }

      .footer-right {
        display: flex;
        align-items: center;
        gap: 1.25rem;
      }

      .footer-link {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #546e7a;
        text-decoration: none;
        transition: color 0.15s ease-in-out;

        .footer-icon {
          font-size: 15px;
          width: 15px;
          height: 15px;
        }

        &:hover {
          color: #1976d2;
          text-decoration: underline;
        }
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  protected auth = inject(AuthService);
  protected toolbarService = inject(ToolbarPortalService);
  private dialog = inject(MatDialog);
  private storageService = inject(StorageService);

  readonly currentYear = new Date().getFullYear();

  @HostListener('window:focus')
  onWindowFocus() {
    // Trigger remote check when user returns to the tab
    this.storageService.checkForRemoteUpdates();
  }

  ngOnInit(): void {
    if (!this.auth.isAuthenticated()) {
      setTimeout(() => {
        this.auth.initGoogleSignIn('google-btn');
      }, 100);
    }
  }

  openSettingsDialog(): void {
    this.dialog.open(SettingsDialogComponent, {
      width: '450px',
      autoFocus: false,
    });
  }
}
